import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { useBook } from '../../../contexts/BookContext';
import { useCredits } from '../../../contexts/CreditsContext';
import { generateTextAudio } from '../../../services/audio/generateTextAudio';

const AUDIO_COST = 3; // match TextElement cost per audio
// Matches AUDIO_LOCK_MS in functions/generateTextAudio.js — a 'pending' audio
// mark this fresh means a generation is already running for the element.
const AUDIO_LOCK_MS = 2 * 60 * 1000;
const isAudioInFlight = (audio) =>
  audio?.status === 'pending' && Date.now() - (audio.pendingAt || audio.updatedAt || 0) < AUDIO_LOCK_MS;

export default function GenerateAllAudioModal({ isOpen, onClose }) {
  const { currentBook, loadBook } = useBook();
  const { checkCredits } = useCredits();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('PREPARING');

  const { totalTextCount, missingAudioCount, allTextElements, missingAudioElements } = useMemo(() => {
    const spreads = currentBook?.spreads || {};
    let total = 0;
    let missing = 0;
    const all = [];
    const missingArr = [];
    Object.entries(spreads).forEach(([spreadId, spread]) => {
      Object.entries(spread?.elements || {}).forEach(([elementId, el]) => {
        if (el?.type === 'text') {
          total += 1;
          const has = !!el?.audio?.url;
          const entry = { ...el, spreadId, elementId };
          all.push(entry);
          if (!has && !isAudioInFlight(el?.audio)) {
            missing += 1;
            missingArr.push(entry);
          }
        }
      });
    });
    return { totalTextCount: total, missingAudioCount: missing, allTextElements: all, missingAudioElements: missingArr };
  }, [currentBook?.spreads]);

  const reset = () => {
    setIsGenerating(false);
    setStatus('PREPARING');
    setProgress(0);
  };

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  const runGeneration = async (mode) => {
    if (!currentBook) return;
    // Skip elements already generating — the server rejects those calls with
    // failed-precondition anyway, this just avoids the wasted round-trips.
    const list = (mode === 'missing' ? missingAudioElements : allTextElements)
      .filter((el) => !isAudioInFlight(el?.audio));
    if (!list.length) return;

    const totalCost = list.length * AUDIO_COST;
    const ok = await checkCredits?.(totalCost);
    if (!ok) {
      alert(`Insufficient credits. You need ${totalCost} credits to generate audio.`);
      return;
    }

    setIsGenerating(true);
    setStatus('GENERATING');
    setProgress(5);

    try {
      // Avoid many parallel requests; run sequentially and update progress.
      // Each call writes its audio into the book server-side, so there is
      // nothing left to save here and no result can be lost if the tab dies.
      let completed = 0;
      let ranOutOfCredits = false;
      for (const element of list) {
        try {
          const { content, spreadId } = element;
          const resp = await generateTextAudio(content || '', {
            bookId: currentBook.id,
            spreadId,
            elementId: element.id || element.elementId,
          });
          if (!resp?.url) throw new Error('No audio URL returned');
        } catch (e) {
          // Each call is charged server-side; stop early when the balance runs
          // dry so the remaining elements are not all failed one by one.
          if (e?.code === 'functions/resource-exhausted') {
            ranOutOfCredits = true;
            break;
          }
          // continue on other errors; could add per-item error list later
        }
        completed += 1;
        setProgress(Math.round(5 + (completed / list.length) * 85));
      }

      setStatus('SAVING');
      setProgress(95);

      if (ranOutOfCredits) {
        alert(`Credits ran out after ${completed} of ${list.length} elements — the rest were skipped.`);
      }

      setStatus('COMPLETING');
      setProgress(100);
      await loadBook(currentBook.id);
      setTimeout(() => { onClose?.(); reset(); }, 800);
    } catch (e) {
      console.error('Error generating narration audio:', e);
      alert('An error occurred while generating audio. Please try again.');
      setIsGenerating(false);
    }
  };

  return (
    <Dialog open={!!isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />

        <div className="relative bg-white rounded-lg max-w-md w-full p-6">
          <div className="absolute top-4 right-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="mt-2">
            <Dialog.Title className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <MusicalNoteIcon className="h-6 w-6 text-indigo-600" />
              Generate Narration Audio
            </Dialog.Title>

            {isGenerating ? (
              <div className="space-y-4">
                <div className="text-center text-gray-600">
                  {status === 'PREPARING' && 'Preparing...'}
                  {status === 'GENERATING' && 'Generating audio files...'}
                  {status === 'SAVING' && 'Saving audio to your book...'}
                  {status === 'COMPLETING' && 'Finalizing...'}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Choose what to generate for this book's narration.
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>Total text elements: <strong>{totalTextCount}</strong></li>
                  <li>Missing audio: <strong>{missingAudioCount}</strong></li>
                  <li>Cost per audio: <strong>{AUDIO_COST} credits</strong></li>
                </ul>
                <div className="space-y-3 pt-2">
                  <button
                    onClick={() => runGeneration('missing')}
                    disabled={missingAudioCount === 0}
                    className={`w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-white ${missingAudioCount === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    Generate Remaining ({missingAudioCount}) - {missingAudioCount * AUDIO_COST} Credits
                  </button>
                  <button
                    onClick={() => runGeneration('all')}
                    disabled={totalTextCount === 0}
                    className={`w-full inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-white ${totalTextCount === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    Generate All ({totalTextCount}) - {totalTextCount * AUDIO_COST} Credits
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
